import React, { useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import MainLayout from "../../../components/layout/MainLayout";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import MedicationSheetForm from "../MedicationSheet/MedicationSheetForm";
import {
    fetchMedicationSheet,
    updateMedicationSheet,
    clearError,
} from "../../../store/slices/medicationSheetsSlice";

const EditMedicationSheet = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};

    const { currentMedicationSheet, isLoading, error } = useSelector(
        (state) => state.medicationSheets
    );

    const [formData, setFormData] = React.useState(null);

    // Format date to YYYY-MM-DD for input fields
    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toISOString().slice(0, 10);
    };

    // Format datetime to YYYY-MM-DDTHH:mm for datetime-local input
    const formatDateTimeLocal = (dateTimeStr) => {
        if (!dateTimeStr) return "";
        const date = new Date(dateTimeStr);
        return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
    };

    // === Fetch on Mount ===
    useEffect(() => {
        dispatch(clearError());
        dispatch(fetchMedicationSheet(id));
    }, [dispatch, id]);

    // === Map fetched data to formData with nested structure ===
    useEffect(() => {
        if (currentMedicationSheet) {
            // Map medications with their time entries
            const medications = currentMedicationSheet.items?.length
                ? currentMedicationSheet.items.map((item) => {
                    // Generate unique IDs for time entries if they don't have them
                    const timeEntries = item.time_entries?.map((timeEntry, index) => ({
                        id: timeEntry.id || Date.now() + index,
                        given: timeEntry.given || "",
                        time_date: formatDateTimeLocal(timeEntry.time_date),
                    })) || [];

                    return {
                        id: item.id,
                        drugName: item.drug_name || "",
                        dose: item.dose || "",
                        frequency: item.frequency || "",
                        sign: item.sign || "",
                        startDate: formatDate(item.start_date),
                        timeEntries: timeEntries,
                    };
                })
                : [
                    {
                        id: Date.now(),
                        drugName: "",
                        dose: "",
                        frequency: "",
                        sign: "",
                        startDate: "",
                        timeEntries: [],
                    },
                ];

            setFormData({
                patientId: patientId || currentMedicationSheet.patientId,
                caseId: caseId || currentMedicationSheet.caseId,
                name: currentMedicationSheet.name || "",
                bedNo: currentMedicationSheet.bed_no || "",
                doa: formatDate(currentMedicationSheet.doa),
                mrNo: currentMedicationSheet.mr_no || "",
                age: currentMedicationSheet.age || "",
                consultant: currentMedicationSheet.consultant || "",
                ward: currentMedicationSheet.ward || "",
                diagnosis: currentMedicationSheet.diagnosis || "",
                documentName: currentMedicationSheet.document_name || "Medication Sheet",
                effectiveDate: formatDate(currentMedicationSheet.effective_date),
                approvedBy: currentMedicationSheet.approved_by || "",
                approvedDate: formatDate(currentMedicationSheet.approved_date),
                nextRevisionBy: currentMedicationSheet.next_revision_by || "",
                nextRevisionDate: formatDate(currentMedicationSheet.next_revision_date),
                patient_id: currentMedicationSheet.patient_id || "",
                case_id: currentMedicationSheet.case_id || "",
                medications: medications,
            });
        }
    }, [currentMedicationSheet, caseId, patientId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData) return;

        // Validation for required fields
        const required = ["name", "doa"];
        const missing = required.filter((f) => !formData[f]);
        if (missing.length > 0) {
            Swal.fire({
                title: "Missing Fields",
                text: `Please fill: ${missing.join(", ")}`,
                icon: "warning",
            });
            return;
        }

        // Validate medications
        if (formData.medications.some((m) => !m.drugName)) {
            Swal.fire({
                title: "Validation Error",
                text: "All medication rows must have a Drug Name.",
                icon: "warning",
            });
            return;
        }

        // Helper: empty string → null
        const toNull = (val) => (val === "" ? null : val);

        // Format date to YYYY-MM-DD format for backend
        const formatDateForBackend = (dateString) => {
            if (!dateString) return null;
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        };

        // Format datetime for time entries
        const formatDateTimeForBackend = (dateTimeString) => {
            if (!dateTimeString) return null;
            // Convert from YYYY-MM-DDTHH:mm to YYYY-MM-DD HH:mm:ss
            return dateTimeString.replace('T', ' ') + ':00';
        };

        // Final payload with snake_case
        const payload = {
            // Master fields
            patient_id: toNull(formData.patientId),
            case_id: toNull(formData.caseId),
            name: formData.name,
            bed_no: toNull(formData.bedNo),
            doa: formatDateForBackend(formData.doa),
            mr_no: toNull(formData.mrNo),
            age: formData.age !== "" && formData.age !== null && formData.age !== undefined ? String(formData.age) : null,
            consultant: toNull(formData.consultant),
            ward: toNull(formData.ward),
            diagnosis: toNull(formData.diagnosis),
            document_name: formData.documentName,
            effective_date: formatDateForBackend(formData.effectiveDate),
            approved_by: toNull(formData.approvedBy),
            approved_date: formatDateForBackend(formData.approvedDate),
            next_revision_by: toNull(formData.nextRevisionBy),
            next_revision_date: formatDateForBackend(formData.nextRevisionDate),

            // Child rows (medications with their time entries)
            medications: formData.medications.map((m) => ({
                drugName: m.drugName,
                dose: toNull(m.dose),
                frequency: toNull(m.frequency),
                sign: toNull(m.sign),
                startDate: formatDateForBackend(m.startDate),
                timeEntries: m.timeEntries.map((timeEntry) => ({
                    given: timeEntry.given || null,
                    time_date: formatDateTimeForBackend(timeEntry.time_date),
                })),
            })),
        };

        console.log("Submitting update payload:", JSON.stringify(payload, null, 2));

        try {
            // Note: Using caseId from state instead of payload.case_id for the update endpoint
            const updateCaseId = caseId || payload.case_id;

            await dispatch(updateMedicationSheet({
                case_id: updateCaseId,
                medicationData: payload
            })).unwrap();

            Swal.fire({
                title: "Updated!",
                text: "Medication sheet updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            // Stay on edit page
        } catch (err) {
            console.error("Error updating medication sheet:", err);

            let errorMsg = "Failed to update medication sheet.";
            if (typeof err === "string") {
                errorMsg = err;
            } else if (typeof err === "object" && err !== null) {
                if (err.errors) {
                    errorMsg = Object.values(err.errors).flat().join("\n");
                } else {
                    errorMsg = Object.values(err).flat().join("\n");
                }
            }

            Swal.fire({
                title: "Error",
                text: errorMsg,
                icon: "error",
            });
        }
    };

    // === Loading State ===
    if (isLoading || !formData) {
        return (
            <MainLayout>
                <div className="text-center mt-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading medication sheet...</p>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/phase05/medication-sheets/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>
                <div className="card">
                    <div className="card-body">
                        {/* Backend Error */}
                        {error && (
                            <div className="alert alert-danger alert-dismissible">
                                <strong>Error:</strong> {typeof error === "object" ? Object.values(error).flat().join("\n") : error}
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => dispatch(clearError())}
                                />
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <MedicationSheetForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                                isLoading={isLoading}
                            />
                            <div className="col-12 d-flex justify-content-end mt-4">
                                <div className="col-md-2 d-flex justify-content-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary px-4"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? (
                                            <>
                                                <i className="ri-loader-4-line me-1 animate-spin"></i> Updating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="ri-edit-2-line me-1"></i> Update
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditMedicationSheet;